import {
  createOddElementMarker,
  getOddIconSrc,
  mapStyle,
  ODD_ICON_DEFS,
  ODD_TOGGLE_TYPES,
  oddLayerId,
  setUpMaplibreWorker,
  type TripAnnotationMarker,
  useAnnotationMapLayer,
} from '@backoffice-frontend/overwatch-map-common'
import type { LngLatBoundsLike, StyleSpecification } from 'maplibre-gl'
import * as maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useCallback, useEffect, useRef, useState } from 'react'
import {
  calculateLineMidpoint,
  cameraForBoundsWithPaddingFallback,
  findNearbyStreetDirections,
  findNearbyStreetDirectionsFromSource,
  HUBS,
  headingSelectionBearings,
  ROUTE_VIOLATION_LABELS,
  SDS_REASON_LABELS,
} from '../utils'
import { isSameSuggestionSet, matchingSuggestedBearing } from './dragHeading'
import {
  addConfirmedHeadingArrow,
  removeHeadingArrowsFromMarker,
  showHeadingSelectionArrows,
} from './HeadingArrow'
import { useItemsAfterMapSettles } from './hooks/useItemsAfterMapSettles'
import type {
  ODDMapElement,
  RouteViolation,
  SdsViolation,
  Waypoint,
} from './types'

export type MapMode = 'overview' | 'editor'

type VehicleMarkerData = {
  location: { latitude: number; longitude: number }
  name: string
  isPhantom?: boolean
  heading?: number | null
}

type RouteMapProps = {
  center: [number, number]
  mode: MapMode
  waypoints: Waypoint[]
  routeCoordinates: [number, number][]
  violations?: RouteViolation[]
  sdsViolations?: SdsViolation[]
  fitWaypoints?: boolean
  fitKey?: string | null
  oddElements?: ODDMapElement[]
  annotations?: TripAnnotationMarker[]
  /**
   * Render the same annotations as a density heatmap underneath the markers.
   * Scoped by whatever `annotations` already carries, so the date range and the
   * master layer toggle apply to both without a second filter.
   */
  showAnnotationHeatmap?: boolean
  vehicleMarker?: VehicleMarkerData
  headings?: Record<string, number>
  pendingHeadingWaypointId?: string | null
  onMapClick?: (lat: number, lng: number) => void
  onMarkerClick?: (waypointId: string) => void
  onMarkerDrag?: (index: number, lat: number, lng: number) => void
  onMarkerDragStart?: (waypointId: string) => void
  /**
   * Fired the moment a waypoint drag begins (not on release), so the parent can
   * reset the route to the orange "ungenerated" state while the marker moves.
   */
  onMarkerMoveStart?: (waypointId: string) => void
  onOddElementClick?: (element: ODDMapElement) => void
  onMapReady?: (map: maplibregl.Map) => void
  onHeadingSelect?: (waypointId: string, bearing: number) => void
  onRemoveHeading?: (waypointId: string) => void
  padding?: { top: number; right: number; bottom: number; left: number }
  /**
   * What the camera does on the editor → overview transition. `true` flies back
   * to `center` (closing a saved route). `false` keeps the current center and
   * zoom and only restores the tilt (cancelling Build Route, where the user
   * wants to stay where they were working).
   */
  recenterOnOverview?: boolean
}

// Persists camera state across map remounts (navigation between routes).
// When SavedTripsView → TripEditorView navigation destroys one RouteMap and
// mounts another, the new instance starts at the saved camera rather than at
// the default 45° overview pitch, eliminating the visible flash.
type CameraSnapshot = {
  center: [number, number]
  zoom: number
  pitch: number
  bearing: number
}
let lastCameraSnapshot: CameraSnapshot | null = null

/**
 * Clear the persisted camera snapshot. Call this when a route-builder session
 * ends (the SharedMapProvider unmounts) so re-entering the area starts fresh at
 * the default 45° overview instead of resuming the zoom/pitch of whatever route
 * was open when the user left. The snapshot only exists to bridge RouteMap
 * remounts *within* one session — it must not leak across area exits.
 */
export const resetRouteMapCameraSnapshot = () => {
  lastCameraSnapshot = null
}

const ROUTE_SOURCE_ID = 'route-line-source'
export const ROUTE_LAYER_ID = 'route-line-layer'
// Fade duration for revealing the route line + markers once the map has expanded
// and the camera fit begins — keeps the reveal in step with the camera flyTo.
const ROUTE_REVEAL_MS = 300
// Debounce after the last observed canvas `resize` before running the corrective
// fit — long enough to coalesce MapLibre's trackResize ResizeObserver ticks
// (~50ms) that fire as the map column's CSS width transition steps, so the
// corrective computes zoom/center against the FINAL canvas rather than a
// mid-transition width.
const RESIZE_SETTLE_MS = 80
// No-resize backstop delay. When the map column's `left` DOES animate, real
// `resize` events arrive within the layout TRANSITION_MS (300ms) and reschedule
// the corrective, so this backstop is cleared before it fires. It only fires when
// the edge never moves (same-layout re-fit, poly upgrade). Set comfortably past
// the transition so it never races a real resize with a wasteful mid-transition
// move.
const TRANSITION_SETTLE_MS = 360
// Corrective camera move duration. Short — it eases from the already-close first
// framing to the settled canvas, so a brief linear nudge reads as one motion.
const CORRECTIVE_MS = 350
// Hard cap on the corrective window after a fit fires. Past this, stop listening
// for resizes so a later user-driven resize (window resize, fullscreen) can't
// re-fit and yank the camera back after the user has panned. Must comfortably
// exceed the layout TRANSITION_MS (300ms) plus the ResizeObserver debounce.
const FIT_SETTLE_WINDOW_MS = 1500
const VIOLATION_SOURCE_ID = 'violation-lines-source'
const VIOLATION_LAYER_ID = 'violation-lines-layer'
const COMPARE_SOURCE_ID = 'compare-lines-source'
const COMPARE_LAYER_ID = 'compare-lines-layer'
const SDS_VIOLATION_SOURCE_ID = 'sds-violation-source'
const SDS_VIOLATION_LAYER_ID = 'sds-violation-layer'
const HEADING_SNAP_SOURCE_ID = 'heading-snap-source'
const HEADING_SNAP_LAYER_ID = 'heading-snap-layer'
const SDS_ERROR_COLOR = '#fc0606'

const OVERVIEW_ZOOM = 13
const OVERVIEW_PITCH = 45
const OVERVIEW_BEARING = -17.6

const GLOW_STYLE_ID = 'route-builder-waypoint-glow-style'

const ensureGlowStyle = () => {
  if (document.getElementById(GLOW_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = GLOW_STYLE_ID
  style.textContent = `
    @keyframes waypoint-pulse {
      0%, 100% { filter: drop-shadow(0 0 6px var(--waypoint-glow-color)); }
      50% { filter: drop-shadow(0 0 12px var(--waypoint-glow-color)); }
    }
  `
  document.head.appendChild(style)
}

const createHubMarkerElement = (name: string): HTMLElement => {
  const el = document.createElement('div')
  el.style.cssText =
    'width:28px;height:28px;cursor:pointer;display:flex;align-items:center;justify-content:center'
  el.innerHTML = `<svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <circle cx="14" cy="14" r="12" fill="#1A237E" stroke="white" stroke-width="2"/>
    <circle cx="14" cy="14" r="4" fill="white"/>
  </svg>`
  el.title = name
  return el
}

const UNVALIDATED_COLOR = '#E87722' // orange
const VALIDATED_COLOR = '#0532ff' // backoffice blue
const ERROR_COLOR = '#D32F2F' // red
const COMPLETED_COLOR = '#2e7d32' // green for completed waypoints

// Orbit radius constants matching overwatch RB_ORBIT_CIRCLE / RB_ORBIT_PILL_RX
const RB_ORBIT_CIRCLE = 25
const RB_ORBIT_PILL_RX = 33

// Reshape an existing marker root element into a standard circle marker
// (matches overwatch markerElement()). Used both when building a plain marker
// and when erasing an ODD pill in-place on drag start — the latter must mutate
// the existing element rather than rebuild, so MapLibre keeps pointer capture.
const applyPlainCircleMarker = (
  div: HTMLElement,
  index: number,
  color: string,
  isCompleted?: boolean,
): void => {
  div.dataset.arrowRx = String(RB_ORBIT_CIRCLE)
  div.dataset.arrowRy = String(RB_ORBIT_CIRCLE)
  div.style.display = ''
  Object.assign(div.style, {
    width: '32px',
    height: '32px',
    zIndex: '100',
  })

  const circle = document.createElement('div')
  Object.assign(circle.style, {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: '32px',
    height: '32px',
    borderRadius: '50%',
    backgroundColor: color,
    border: '2px solid white',
    outline: '1px solid rgba(0,0,0,0.25)',
    boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
    cursor: 'pointer',
  })

  if (isCompleted) {
    // Checkmark SVG for completed waypoints
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    svg.setAttribute('width', '16')
    svg.setAttribute('height', '16')
    svg.setAttribute('viewBox', '0 0 16 16')
    svg.setAttribute('fill', 'none')
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path')
    path.setAttribute('d', 'M3 8l3.5 3.5L13 4.5')
    path.setAttribute('stroke', 'white')
    path.setAttribute('stroke-width', '2')
    path.setAttribute('stroke-linecap', 'round')
    path.setAttribute('stroke-linejoin', 'round')
    svg.appendChild(path)
    circle.appendChild(svg)
  } else {
    const span = document.createElement('span')
    span.textContent = String(index)
    Object.assign(span.style, {
      color: 'white',
      fontWeight: '600',
      fontSize: '13px',
      lineHeight: '1',
      fontFamily: 'sans-serif',
    })
    circle.appendChild(span)
  }

  div.replaceChildren(circle)
}

// Switch a marker to the orange "ungenerated" look IN PLACE, without replacing
// the circle element. Used on drag start: the circle child holds the active
// pointer capture and hosts the heading-arrow overlays, so replacing it would
// interrupt the drag and stop live arrow recalculation. This only recolors and,
// for ODD pills, strips the icon + reshapes to a circle — leaving the element
// (and its captured pointer) intact.
const markMarkerUngeneratedInPlace = (
  markerEl: HTMLElement,
  isCompleted?: boolean,
): void => {
  const circle = markerEl.querySelector('div') as HTMLElement | null
  if (!circle) return

  if (!isCompleted) {
    circle.style.backgroundColor = UNVALIDATED_COLOR
    // Enter the pending-selection glow immediately. The heading-arrow effect
    // skips the marker while it's being dragged, so it can't apply this itself.
    const glowColor = 'rgba(232, 119, 34, 0.7)'
    circle.style.setProperty('--waypoint-glow-color', glowColor)
    circle.style.animation = 'waypoint-pulse 1.2s ease-in-out infinite'
    circle.style.filter = `drop-shadow(0 0 6px ${glowColor})`
  }

  // ODD pill → plain circle: drop the icon and reset the pill-specific styles.
  const icon = circle.querySelector('img')
  if (icon) {
    icon.remove()
    Object.assign(circle.style, {
      justifyContent: 'center',
      width: '32px',
      height: '32px',
      borderRadius: '50%',
      padding: '0',
      gap: '0',
    })
    Object.assign(markerEl.style, {
      display: '',
      width: '32px',
      height: '32px',
    })
    markerEl.dataset.arrowRx = String(RB_ORBIT_CIRCLE)
    markerEl.dataset.arrowRy = String(RB_ORBIT_CIRCLE)
  }
}

const createMarkerElement = (
  index: number,
  color: string,
  oddType?: string | null,
  isCompleted?: boolean,
): HTMLElement => {
  // Outer wrapper: 32×32 for circle markers, wider pill for ODD markers
  const div = document.createElement('div')
  const oddDef = oddType ? ODD_ICON_DEFS[oddType] : null

  if (oddDef) {
    // ODD-typed: pill shape — matches overwatch waypoint-with-odd
    const circle = document.createElement('div')
    const numberStr = String(index)
    const pillRx = RB_ORBIT_PILL_RX + Math.max(0, numberStr.length - 1) * 4
    div.dataset.arrowRx = String(pillRx)
    div.dataset.arrowRy = String(RB_ORBIT_CIRCLE)
    Object.assign(div.style, {
      zIndex: '100',
      display: 'inline-flex',
    })
    Object.assign(circle.style, {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
      height: '32px',
      borderRadius: '18px',
      padding: '0 6px 0 4px',
      gap: '5px',
      backgroundColor: color,
      border: '2px solid white',
      outline: '1px solid rgba(0,0,0,0.25)',
      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
      cursor: 'pointer',
    })

    const iconWrap = document.createElement('img')
    iconWrap.src = getOddIconSrc(oddType!)
    iconWrap.width = 20
    iconWrap.height = 20
    iconWrap.style.flexShrink = '0'
    iconWrap.style.display = 'block'

    const label = document.createElement('span')
    label.textContent = isCompleted ? '✓' : numberStr
    Object.assign(label.style, {
      color: 'white',
      fontWeight: '600',
      fontSize: '13px',
      lineHeight: '1',
      fontFamily: 'sans-serif',
    })

    circle.appendChild(iconWrap)
    circle.appendChild(label)
    div.appendChild(circle)
  } else {
    applyPlainCircleMarker(div, index, color, isCompleted)
  }

  return div
}

export const RouteMap = ({
  center,
  mode,
  waypoints,
  routeCoordinates,
  violations = [],
  sdsViolations = [],
  fitWaypoints = false,
  fitKey,
  oddElements = [],
  annotations = [],
  showAnnotationHeatmap = false,
  vehicleMarker,
  headings = {},
  pendingHeadingWaypointId = null,
  onMapClick,
  onMarkerClick,
  onMarkerDrag,
  onMarkerDragStart,
  onMarkerMoveStart,
  onOddElementClick,
  onMapReady,
  onHeadingSelect,
  onRemoveHeading,
  padding,
  recenterOnOverview = true,
}: RouteMapProps) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const mapRef = useRef<maplibregl.Map | null>(null)
  // The same map as `mapRef`, published as state so hooks that take the map as
  // an argument (`useAnnotationMapLayer`) re-run once it exists. A ref alone
  // can't do that -- writing to it doesn't render -- and everything else in this
  // file reaches the map from inside an effect or a listener, where the ref is
  // enough. Set on `load` alongside `onMapReady`.
  const [mapInstance, setMapInstance] = useState<maplibregl.Map | null>(null)
  const markersRef = useRef<maplibregl.Marker[]>([])
  const markerWaypointIdsRef = useRef<string[]>([])
  const oddMarkersRef = useRef<maplibregl.Marker[]>([])
  const sdsMarkersRef = useRef<maplibregl.Marker[]>([])
  const violationMarkersRef = useRef<maplibregl.Marker[]>([])
  const waypointsRef = useRef(waypoints)
  const vehicleMarkerRef = useRef<maplibregl.Marker | null>(null)
  const mapLoadedRef = useRef(false)
  const onMapClickRef = useRef(onMapClick)
  const onMarkerClickRef = useRef(onMarkerClick)
  const onMarkerDragRef = useRef(onMarkerDrag)
  const onMarkerDragStartRef = useRef(onMarkerDragStart)
  const onMarkerMoveStartRef = useRef(onMarkerMoveStart)
  const onOddElementClickRef = useRef(onOddElementClick)
  const onMapReadyRef = useRef(onMapReady)
  const onHeadingSelectRef = useRef(onHeadingSelect)
  const onRemoveHeadingRef = useRef(onRemoveHeading)

  waypointsRef.current = waypoints
  onMapClickRef.current = onMapClick
  onMarkerClickRef.current = onMarkerClick
  onMarkerDragRef.current = onMarkerDrag
  onMarkerDragStartRef.current = onMarkerDragStart
  onMarkerMoveStartRef.current = onMarkerMoveStart
  onOddElementClickRef.current = onOddElementClick
  onMapReadyRef.current = onMapReady
  onHeadingSelectRef.current = onHeadingSelect
  onRemoveHeadingRef.current = onRemoveHeading

  // Stable ref to current pendingHeadingWaypointId — readable from drag handlers
  const pendingHeadingWaypointIdRef = useRef(pendingHeadingWaypointId)
  pendingHeadingWaypointIdRef.current = pendingHeadingWaypointId
  // Current confirmed headings — read on dragstart, whose handler is bound once
  // per marker and would otherwise see stale headings.
  const headingsRef = useRef(headings)
  headingsRef.current = headings

  // Tracks which waypoint marker is actively being dragged.
  // The heading effect must NOT call showHeadingSelectionArrows while a drag is
  // in progress — those DOM buttons intercept the active pointer and break the drag.
  const draggingWaypointIdRef = useRef<string | null>(null)

  // All per-waypoint snap line coordinates — keyed by waypointId.
  // Written by both the heading effect (bulk) and the drag handler (single entry).
  type SnapEntry = {
    fromLng: number
    fromLat: number
    snapLng: number
    snapLat: number
  }
  const snapCoordsRef = useRef<Map<string, SnapEntry>>(new Map())

  // Stable ref to the refresh function so the drag handler can call it without
  // capturing a stale closure. Set once the map source exists.
  const refreshSnapLinesRef = useRef(() => {})

  // Guards against the general map.on('click') firing when an ODD DOM marker was clicked.
  // MapLibre tracks pointer events internally; e.stopPropagation() on the DOM marker does
  // not prevent map.on('click') from also firing. We set this flag synchronously in the
  // marker click handler and clear it on the next animation frame, which is always after
  // MapLibre's click handler runs.
  const oddMarkerClickedRef = useRef(false)

  const initialCenterRef = useRef(center)
  const initialModeRef = useRef(mode)

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return

    setUpMaplibreWorker()
    ensureGlowStyle()

    // The snapshot only bridges an *in-session, editor-mode* remount so the flat
    // route view doesn't flash back to the tilted overview. A brand-new RouteMap
    // in overview mode means we're (re)entering the area at the saved-trips list —
    // there we must always start at the default 45° overview, never resume the
    // zoom/pitch/center of whatever route was open when the user last left. So
    // resume from the snapshot ONLY in editor mode; otherwise ignore it entirely.
    // This makes the entry camera deterministic and independent of whether the
    // unmount-time snapshot clear actually ran.
    const snapshot =
      initialModeRef.current === 'editor' ? lastCameraSnapshot : null
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: mapStyle as StyleSpecification,
      center: snapshot?.center ?? initialCenterRef.current,
      zoom: snapshot?.zoom ?? OVERVIEW_ZOOM,
      pitch: snapshot?.pitch ?? OVERVIEW_PITCH,
      bearing: snapshot?.bearing ?? OVERVIEW_BEARING,
      attributionControl: false,
      // Keep MapLibre's own resize tracking ON — it resizes the canvas live as the
      // map column grows/shrinks (panel open/close), so neither transition leaves
      // an unpainted grey strip, and it keeps the map's geographic center fixed
      // across each resize. That lets the route-fit fly-to-center animate
      // concurrently with the panel-OPEN shrink and stay framed throughout.
      trackResize: true,
    })

    map.on('load', () => {
      mapLoadedRef.current = true
      setMapInstance(map)
      onMapReadyRef.current?.(map)
      if (initialModeRef.current === 'editor') {
        // If we resumed from a snapshot already near 0° pitch this is a no-op
        // visually. If coming from overview it smoothly transitions.
        map.easeTo({
          pitch: 0,
          bearing: 0,
          zoom: snapshot?.zoom ?? 14,
          duration: snapshot ? 300 : 600,
        })
        map.getCanvas().style.cursor = 'crosshair'
      }

      // Add hub markers
      for (const hub of HUBS) {
        new maplibregl.Marker({
          element: createHubMarkerElement(hub.name),
          anchor: 'center',
        })
          .setLngLat(hub.coords)
          .addTo(map)
      }
    })

    map.on('click', (e: maplibregl.MapMouseEvent) => {
      // Suppress plain waypoint when an ODD DOM marker was just clicked.
      // The flag is set on pointerdown (before MapLibre fires click) and cleared
      // on the next animation frame.
      if (oddMarkerClickedRef.current) return
      // Don't place a plain waypoint when the click lands on an ODD symbol layer —
      // MapOverlayOptions has its own per-layer handler that creates an ODD waypoint.
      // Filter to only layers that exist on the map to avoid queryRenderedFeatures throwing.
      const oddLayerIds = ODD_TOGGLE_TYPES.map(t => oddLayerId(t.type)).filter(
        id => map.getLayer(id),
      )
      if (oddLayerIds.length > 0) {
        const hit = map.queryRenderedFeatures(e.point, { layers: oddLayerIds })
        if (hit.length > 0) return
      }
      if (onMapClickRef.current) {
        onMapClickRef.current(e.lngLat.lat, e.lngLat.lng)
      }
    })

    mapRef.current = map

    return () => {
      // Save camera so the next RouteMap instance (after navigation) starts here
      lastCameraSnapshot = {
        center: map.getCenter().toArray() as [number, number],
        zoom: map.getZoom(),
        pitch: map.getPitch(),
        bearing: map.getBearing(),
      }
      if (fitTimerRef.current) clearTimeout(fitTimerRef.current)
      correctiveCleanupRef.current?.()
      if (correctiveFitTimerRef.current) {
        clearTimeout(correctiveFitTimerRef.current)
      }
      map.remove()
      mapRef.current = null
      mapLoadedRef.current = false
      setMapInstance(null)
    }
  }, [])

  // fitWaypoints ref — read synchronously by other effects to suppress competing camera moves
  const fitWaypointsRef = useRef(fitWaypoints)
  fitWaypointsRef.current = fitWaypoints

  // Fly to new center when metropolis location changes
  const prevCenterRef = useRef(center)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (
      prevCenterRef.current[0] === center[0] &&
      prevCenterRef.current[1] === center[1]
    )
      return
    prevCenterRef.current = center
    // Suppress if fitWaypoints will reposition the map
    if (fitWaypointsRef.current) return

    if (mode === 'editor') {
      map.flyTo({ center, zoom: 14, pitch: 0, bearing: 0, duration: 800 })
    } else {
      map.flyTo({
        center,
        zoom: OVERVIEW_ZOOM,
        pitch: OVERVIEW_PITCH,
        bearing: OVERVIEW_BEARING,
        duration: 1200,
      })
    }
  }, [center, mode])

  // Handle mode transitions
  const prevModeRef = useRef(mode)
  useEffect(() => {
    const map = mapRef.current
    if (!map) return
    if (prevModeRef.current === mode) return
    prevModeRef.current = mode

    if (mode === 'editor') {
      map.getCanvas().style.cursor = 'crosshair'
      // Suppress if fitWaypoints will handle pitch/bearing/position
      if (!fitWaypointsRef.current) {
        map.easeTo({ pitch: 0, bearing: 0, duration: 500 })
      }
    } else {
      map.getCanvas().style.cursor = ''
      // Suppress the overview move if a fit is about to run (e.g. navigating
      // to edit route — TripEditorView will fire fitWaypoints shortly after
      // this transition, and two competing camera moves would cancel each
      // other and produce no zoom-to-route).
      if (fitWaypointsRef.current) return
      if (recenterOnOverview) {
        // Closing a saved route: fly back to the metropolis overview.
        map.flyTo({
          center,
          zoom: OVERVIEW_ZOOM,
          pitch: OVERVIEW_PITCH,
          bearing: OVERVIEW_BEARING,
          duration: 800,
        })
      } else {
        // Cancelling Build Route: restore only the tilt and keep the user's
        // center and zoom. Flying to the metropolis center here threw the
        // user to the metro origin, which can be far from where they were
        // building the route.
        map.easeTo({
          pitch: OVERVIEW_PITCH,
          bearing: OVERVIEW_BEARING,
          duration: 800,
        })
      }
    }
  }, [mode, center, recenterOnOverview])

  // Keep a ref to the latest padding so fit can read it synchronously
  const paddingRef = useRef(padding)
  paddingRef.current = padding

  // Latest route polyline, read synchronously by the fit so the whole curve is
  // framed — not just the waypoint corners, which a curving route overshoots.
  const routeCoordinatesRef = useRef(routeCoordinates)
  routeCoordinatesRef.current = routeCoordinates

  // Reveal gate for the route line + waypoint markers. On a fresh selection the
  // route is hidden while the map expands (the details card opening), then faded
  // in as the camera flies to frame it — so the sequence reads "expand, then
  // render + centre + zoom" instead of the route flashing at the pre-fit framing
  // mid-resize. Defaults to visible so the editor, modal, and drag flows (which
  // never hide) are unaffected. Marker creation and route-line updates both read
  // this ref so content built while hidden starts hidden.
  const routeVisibleRef = useRef(true)
  const setRouteVisible = useCallback((visible: boolean) => {
    routeVisibleRef.current = visible
    const map = mapRef.current
    if (map?.getLayer(ROUTE_LAYER_ID)) {
      map.setPaintProperty(ROUTE_LAYER_ID, 'line-opacity', visible ? 0.8 : 0)
    }
    for (const m of markersRef.current) {
      const el = m.getElement()
      el.style.transition = `opacity ${ROUTE_REVEAL_MS}ms ease`
      el.style.opacity = visible ? '1' : '0'
    }
  }, [])

  // True when the incoming waypoints are the SAME set the map is already showing —
  // a re-fit for the same route (details → edit, poly upgrade), not a switch to a
  // different trip or a fresh selection from an empty list. The reveal gate uses
  // this to keep an already-painted route visible across a new fitKey (easing to
  // the new framing) instead of blinking it out and re-revealing it. Compared as a
  // set so waypoint reordering doesn't count as a different route; markers still
  // reflect the previous commit here (the marker-build effect runs later).
  const isSameRenderedRoute = useCallback((next: Waypoint[]) => {
    const current = markerWaypointIdsRef.current
    if (current.length === 0 || current.length !== next.length) return false
    const currentIds = new Set(current)
    return next.every(w => currentIds.has(w.id))
  }, [])

  // Close the reveal gate SYNCHRONOUSLY at render time for a brand-new fit
  // session — before any effect runs or any marker/route line is (re)built this
  // commit. Doing this only in the arming effect left a gap: the effect
  // early-returns while the trip's waypoints are still loading (empty), so on the
  // later render where the data arrives the marker-build effect could run with the
  // gate still open and paint the waypoints at full opacity — visible before the
  // shrink finished and before runFit could reveal them. Comparing fitKey against
  // the last-gated key here guarantees every marker built for a new session starts
  // hidden, regardless of effect ordering or which render the data lands on.
  // Same-key re-renders (poly upgrade, waypoint drag) never re-close, so the
  // already-visible route doesn't flash. Deselect resets the key so reselecting the
  // same trip is treated as new.
  const gateKeyRef = useRef<string | null | undefined>(undefined)
  if (fitWaypoints && fitKey) {
    if (fitKey !== gateKeyRef.current) {
      gateKeyRef.current = fitKey
      // Only hide when this is NOT a continuation of the route already on screen.
      // The details → edit transition pushes a new fitKey (`edit-<id>`) for the
      // SAME route that is already visible and framed; hiding it there would blink
      // the markers (and line) out and back as runFit re-reveals them. So keep it
      // visible for the same waypoint set and let runFit ease to the new framing.
      // A fresh selection (empty map) or a switch to a different trip still hides.
      if (!isSameRenderedRoute(waypoints)) {
        routeVisibleRef.current = false
      }
    }
  } else {
    gateKeyRef.current = null
  }

  // Fit the camera to the selected route.
  //
  // The camera move is deferred through a short debounced scheduler rather than
  // firing synchronously, so it reads the freshest geometry: waypoints arrive on
  // render N and the route polyline on render N+1 (later still for legacy routes
  // that re-validate over the network), and an immediate fit would frame the
  // waypoint corners and clip the curve. The debounce only waits for the DATA to
  // settle — NOT the panel width. The fly fires while the map column is still
  // shrinking, so the zoom/center and the panel open animate together.
  //
  // But the card swap changes the canvas width, and cameraForBounds computes
  // zoom/center against the canvas AS IT IS when the fit fires (mid-transition).
  // Once the width settles the route no longer fits — it clips and drifts off
  // centre. So the fit runs TWICE: an immediate fly for the concurrent-with-open
  // motion, then a corrective fly once the canvas `resize` events settle (see
  // runFit) that recomputes against the FINAL canvas. The corrective fly is short
  // and starts from the already-close first framing, so it reads as one settling
  // motion.
  //
  // fitStateRef tracks which session was fitted and whether that fit already
  // included the polyline. A new fitKey always (re)arms; a fit that ran without
  // the poly (legacy route) gets one upgrade re-fit when the poly lands. Waypoint
  // drags in the editor never re-fit: same key, poly already included.
  const fitStateRef = useRef<{
    key: string | null | undefined
    hadPoly: boolean
  }>({ key: undefined, hadPoly: false })
  const fitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const correctiveFitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )
  // The fitKey for which a purposeful framing FLY has actually executed. This —
  // not "did the poly arrive early" — is what decides fly vs ease in runFit: the
  // first fit of a session must always fly (parabolic, flattens the 45° overview
  // tilt, zooms/centres on the route from wherever the camera was), and only a
  // polyline that lands AFTER that fly gets the gentle ease-to-reframe. On a warm
  // cache the polyline arrives within the fit debounce, so the arming effect would
  // otherwise flag an "upgrade" before any fly ran — easing from the untouched
  // overview camera, which skips the flatten + zoom-to-route entirely (the
  // non-focused-list → edit regression). Reset on deselect so reselecting the same
  // trip flies again.
  const fittedFlyKeyRef = useRef<string | null | undefined>(undefined)
  // Cleanup for the resize-driven corrective armed by the current fit session —
  // detaches the map 'resize' listener and clears its pending timers. Held in a
  // ref so a new fit (or unmount) tears down the previous session's listener.
  const correctiveCleanupRef = useRef<(() => void) | null>(null)

  // Compute + move the camera to the route framing against the map's CURRENT
  // canvas size. Returns true if a multi-point fit ran (so the caller can
  // schedule a corrective re-fit once the width settles).
  //
  // `mode` picks the transition: 'fly' (default) is the parabolic flyTo used for
  // the initial framing — it reads as a purposeful zoom-out-then-in from wherever
  // the camera was. 'ease' is a linear easeTo used for the corrective: it starts
  // from the already-close first framing and only nudges center/zoom to the
  // settled canvas, so a linear move looks like one continuous settle rather than
  // a second parabola re-accelerating mid-animation.
  const flyToRouteFraming = useCallback(
    (duration: number, mode: 'fly' | 'ease' = 'fly'): boolean => {
      const map = mapRef.current
      if (!map) return false
      const freshWaypoints = waypointsRef.current.filter(
        w => w.lat != null && w.lng != null,
      )
      if (freshWaypoints.length === 0) return false
      const freshPoly = routeCoordinatesRef.current
      const currentPadding = paddingRef.current ?? {
        top: 60,
        right: 60,
        bottom: 60,
        left: 60,
      }

      if (freshWaypoints.length >= 2 || freshPoly.length >= 2) {
        const bounds = freshWaypoints.reduce(
          (b, w) => b.extend([w.lng!, w.lat!]),
          new maplibregl.LngLatBounds(),
        )
        // Extend to the full route polyline so a route that curves outside the
        // waypoint box is framed whole, not clipped at the bends.
        for (const coord of freshPoly) {
          bounds.extend(coord)
        }
        // Pass bearing:0 so cameraForBounds computes for the flat target view, not
        // the current rotated view (bearing=-17.6). This fixes horizontal misalignment.
        // Do NOT pass padding to flyTo — MapLibre stores it in the transform, so a
        // second cameraForBounds({padding:p}) call would see 2×p → negative usable
        // width → returns undefined → no camera move. cameraForBounds already
        // encodes the padding into the center/zoom it returns.
        // The padding-fallback wrapper handles the assign-trip modal, whose narrower
        // canvas can't fit the full panel padding (cameraForBounds would return
        // undefined and the fit would silently no-op). It also calls map.resize()
        // so the fit is computed against the map's current width.
        // maxZoom 15.5 (not 14) so a geographically small route zooms in tight
        // enough to fill the padded viewport instead of floating with large empty
        // margins between the panels; 14 left sub-kilometre routes badly framed.
        const camera = cameraForBoundsWithPaddingFallback(
          map,
          bounds as LngLatBoundsLike,
          currentPadding,
          { maxZoom: 15.5, bearing: 0, pitch: 0 },
        )
        if (camera) {
          const cameraOptions = {
            center: camera.center,
            zoom: camera.zoom,
            pitch: 0,
            bearing: 0,
            duration,
          }
          if (mode === 'ease') {
            map.easeTo(cameraOptions)
          } else {
            map.flyTo(cameraOptions)
          }
        }
        return true
      }

      const singleWaypointOptions = {
        center: [freshWaypoints[0].lng!, freshWaypoints[0].lat!] as [
          number,
          number,
        ],
        zoom: 14,
        pitch: 0,
        bearing: 0,
        duration,
      }
      if (mode === 'ease') {
        map.easeTo(singleWaypointOptions)
      } else {
        map.flyTo(singleWaypointOptions)
      }
      return false
    },
    [],
  )

  const runFit = useCallback(() => {
    const map = mapRef.current
    if (!map) return
    const fitKey = fitStateRef.current.key
    const freshWaypoints = waypointsRef.current.filter(
      w => w.lat != null && w.lng != null,
    )
    if (freshWaypoints.length === 0) {
      // Nothing to fit yet.
      return
    }
    const hadPoly = routeCoordinatesRef.current.length >= 2
    fitStateRef.current = { key: fitKey, hadPoly }

    // Fly vs ease is decided by whether a purposeful framing FLY has already run
    // for THIS session — not by whether the poly arrived early. The first fit of a
    // session must always fly: it flattens the 45° overview tilt and zooms/centres
    // on the route from wherever the camera was (this is what "rotates like create
    // new route and zooms/centres like details → edit" means). Only once that fly
    // has run does a later-arriving polyline get the gentle ease-to-reframe.
    //
    // On a warm cache the polyline lands within the 60ms fit debounce, so runFit's
    // FIRST invocation already sees the poly. Keying off `pendingFitIsUpgradeRef`
    // (set by the arming effect the moment the poly arrives) would then ease from
    // the untouched overview camera — no flatten, no zoom-to-route. Keying off
    // fittedFlyKeyRef instead guarantees the first fit always flies regardless of
    // when the poly lands.
    const alreadyFlew = fittedFlyKeyRef.current === fitKey

    // Immediate move — a fresh session flies (parabolic, purposeful, animates with
    // the panel shrink); a poly landing after the fly gently eases to reframe.
    // Crucially the corrective below is armed either way: the poly can land
    // mid-layout-transition (warm cache), so the canvas is often still resizing,
    // and without the corrective the framing would lock to a mid-transition size
    // and end up mis-zoomed / off-centre.
    const isMultiPoint = alreadyFlew
      ? flyToRouteFraming(CORRECTIVE_MS, 'ease')
      : flyToRouteFraming(700)
    // Record that this session has flown so a later poly upgrade eases.
    if (!alreadyFlew) fittedFlyKeyRef.current = fitKey
    // Reveal now so the route + waypoints fade in AS the camera moves and the
    // panel shrinks — the zoom/center and the open animation run together.
    setRouteVisible(true)

    // Corrective fly once the canvas width has settled, so the whole route is
    // framed against the FINAL (narrowed/widened) canvas rather than the size the
    // first fly saw. Only for multi-point fits — a single waypoint centres at a
    // fixed zoom regardless of width, so no correction is needed.
    //
    // The map column's `left` animates over TRANSITION_MS as cards swap, and the
    // distance varies wildly: editing from a saved-trip card barely moves the
    // edge (~40px), but editing a trip that was open in the details panel closes
    // that panel AND opens the editor — the left edge jumps ~396px. A single
    // fixed delay (the old FIT_SETTLE_MS) cannot settle both: it fires mid-slide
    // on the long transition, framing the route for an intermediate width so it
    // lands off to one side.
    //
    // Instead, drive the corrective off the map's real `resize` events —
    // MapLibre's trackResize ResizeObserver emits one each time the CSS transition
    // steps the canvas width. Debounce so the fit runs once resizes go quiet (the
    // transition has ended), and cap the whole window so a later user-driven
    // resize (window resize, fullscreen) can't yank the camera back after they've
    // panned. This settles correctly no matter how far — or how many transitions —
    // the edge travels.
    correctiveCleanupRef.current?.()
    correctiveCleanupRef.current = null
    if (isMultiPoint) {
      let settled = false
      const settleWindow = setTimeout(() => {
        settled = true
        cleanup()
      }, FIT_SETTLE_WINDOW_MS)
      const onResize = () => {
        if (settled) return
        if (correctiveFitTimerRef.current) {
          clearTimeout(correctiveFitTimerRef.current)
        }
        // easeTo (linear) from the already-close first framing so the correction
        // reads as one continuous settle, not a second parabola re-accelerating.
        correctiveFitTimerRef.current = setTimeout(() => {
          flyToRouteFraming(CORRECTIVE_MS, 'ease')
        }, RESIZE_SETTLE_MS)
      }
      const cleanup = () => {
        clearTimeout(settleWindow)
        if (correctiveFitTimerRef.current) {
          clearTimeout(correctiveFitTimerRef.current)
          correctiveFitTimerRef.current = null
        }
        map.off('resize', onResize)
        correctiveCleanupRef.current = null
      }
      map.on('resize', onResize)
      correctiveCleanupRef.current = cleanup
      // No-resize backstop: if the edge never moves (re-fit at the same layout,
      // poly upgrade), no `resize` fires, so correct once anyway. Delayed past the
      // layout TRANSITION so that when the edge DOES move, real resizes arrive
      // first and reschedule this via onResize — the backstop never fires a
      // wasteful mid-transition move that the resize corrective would immediately
      // redo (that double move was the visible lag).
      correctiveFitTimerRef.current = setTimeout(() => {
        flyToRouteFraming(CORRECTIVE_MS, 'ease')
      }, TRANSITION_SETTLE_MS)
    }
  }, [setRouteVisible, flyToRouteFraming])

  // Debounce: (re)start the settle timer. Each arming render (waypoints, then the
  // polyline a render later) pushes it back, so the fit runs once the DATA has
  // gone quiet — it does NOT wait for the panel width. A short 60ms window
  // coalesces the waypoint/polyline renders while still firing near-immediately,
  // so the fly starts while the map column is mid-shrink and the two animate
  // together.
  const scheduleFit = useCallback(() => {
    if (fitTimerRef.current) clearTimeout(fitTimerRef.current)
    fitTimerRef.current = setTimeout(runFit, 60)
  }, [runFit])

  useEffect(() => {
    if (!fitWaypoints || !fitKey) {
      if (fitTimerRef.current) clearTimeout(fitTimerRef.current)
      correctiveCleanupRef.current?.()
      if (correctiveFitTimerRef.current) {
        clearTimeout(correctiveFitTimerRef.current)
      }
      fitStateRef.current = { key: fitKey, hadPoly: false }
      // Reset the fly bookkeeping so reselecting the same trip flies again.
      fittedFlyKeyRef.current = undefined
      // Never leave the route hidden when the fit session ends (e.g. deselect
      // before runFit could reveal it).
      setRouteVisible(true)
      return
    }
    const valid = waypoints.filter(w => w.lat != null && w.lng != null)
    if (valid.length === 0) return

    const hasPoly = routeCoordinates.length >= 2
    const st = fitStateRef.current
    const isNewKey = st.key !== fitKey
    const isPolyUpgrade = st.key === fitKey && !st.hadPoly && hasPoly
    if (!isNewKey && !isPolyUpgrade) return

    // On a brand-new selection, hide the route until the fit fires — so it
    // renders + centres + zooms as one revealed motion, fading in as the camera
    // flies and the panel shrinks, rather than flashing at the pre-fit framing.
    // A poly upgrade keeps the already-visible route shown (no re-hide flash) and
    // simply re-frames when the geometry lands. A new key for the SAME route that
    // is already painted (details → edit of the same trip) likewise stays visible —
    // hiding it would blink the markers out and back — and just eases to the new
    // framing; the synchronous gate above uses the same continuation guard.
    if (isNewKey && !isSameRenderedRoute(waypoints)) setRouteVisible(false)

    // Claim the session now so same-key re-renders (e.g. waypoint drags) don't
    // re-arm unless they bring the poly upgrade. hadPoly is finalised by runFit.
    fitStateRef.current = {
      key: fitKey,
      hadPoly: st.key === fitKey && st.hadPoly,
    }
    // A brand-new key hasn't flown yet, so runFit's first pass will fly; a same-key
    // poly upgrade leaves fittedFlyKeyRef alone so runFit eases IF the fly already
    // ran, or still flies if the poly beat runFit (warm cache). runFit itself owns
    // the fly-vs-ease decision via fittedFlyKeyRef.
    scheduleFit()

    // NO per-run cleanup that clears fitTimerRef. This effect re-runs whenever
    // waypoints/routeCoordinates identity changes as edit data settles — often
    // within the 60ms fit debounce. A cleanup here would clear the pending
    // runFit timer, and the immediate re-run then early-returns (session already
    // claimed, poly not yet present → neither isNewKey nor isPolyUpgrade), so the
    // fit is never rescheduled and the camera never moves (the unfocused-list →
    // edit regression). The timer is safely cleared elsewhere: scheduleFit()
    // clears the prior timer before arming a new one, and the deselect branch
    // above and the map-unmount handler both clear it.
  }, [
    fitWaypoints,
    fitKey,
    waypoints,
    routeCoordinates,
    scheduleFit,
    setRouteVisible,
    isSameRenderedRoute,
  ])

  // Update waypoint markers
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Preserve the marker currently being dragged: removing/recreating its DOM
    // element mid-drag releases MapLibre's pointer capture and snaps it back.
    // This lets the move-start reset (validated=false, ODD cleared) run without
    // interrupting the drag — the dragged marker's orange appearance is applied
    // imperatively in the 'dragstart' handler.
    const draggingId = draggingWaypointIdRef.current
    let preservedMarker: maplibregl.Marker | null = null

    for (let i = 0; i < markersRef.current.length; i++) {
      if (draggingId && markerWaypointIdsRef.current[i] === draggingId) {
        preservedMarker = markersRef.current[i]
        continue
      }
      markersRef.current[i].remove()
    }
    markersRef.current = []
    markerWaypointIdsRef.current = []

    for (const [idx, wp] of waypoints.entries()) {
      if (wp.lat == null || wp.lng == null) continue

      if (preservedMarker && wp.id === draggingId) {
        // Keep the live dragged marker as-is (already recolored on dragstart).
        markersRef.current.push(preservedMarker)
        markerWaypointIdsRef.current.push(wp.id)
        preservedMarker = null
        continue
      }

      const isCompleted = wp.status === 'completed'
      const color = isCompleted
        ? COMPLETED_COLOR
        : wp.validated
          ? VALIDATED_COLOR
          : UNVALIDATED_COLOR
      const el = createMarkerElement(
        idx + 1,
        color,
        wp.oddElementType,
        isCompleted,
      )

      const capturedWpId = wp.id
      el.addEventListener('click', e => {
        e.stopPropagation()
        onMarkerClickRef.current?.(capturedWpId)
      })

      // Respect the reveal gate: markers rebuilt while the route is hidden (e.g.
      // waypoints arriving during the expand) must start hidden, then fade in with
      // the rest when runFit reveals them.
      if (!routeVisibleRef.current) {
        el.style.opacity = '0'
      }

      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'center',
        draggable: !!onMarkerDragRef.current,
      })
        .setLngLat([wp.lng, wp.lat])
        .addTo(map)

      if (onMarkerDragRef.current) {
        const capturedIdx = idx
        const capturedWpIdForDrag = wp.id
        // The dragged waypoint's heading as the drag goes on. Null once removed
        // (or if it had none).
        let keptHeading: number | null = null
        // Suggestions at the previous drag tick, to notice when they recalculate.
        let lastSuggestions: number[] | null = null

        // Recompute the snap line and heading-selection arrows for the current
        // marker position. Runs on every 'drag' tick AND once at 'dragstart' so
        // the pending-selection UI (arrows) appears the instant the move begins,
        // rather than only after the pointer first moves.
        const updateDragHeading = () => {
          const pos = marker.getLngLat()
          const lngLat = { lng: pos.lng, lat: pos.lat }
          let result = findNearbyStreetDirectionsFromSource(map, lngLat)
          if (result.directions.length === 0) {
            result = findNearbyStreetDirections(map, lngLat)
          }
          const snap = result.snapPoint

          // Update snap line for this waypoint and refresh all lines
          if (snap) {
            snapCoordsRef.current.set(capturedWpIdForDrag, {
              fromLng: pos.lng,
              fromLat: pos.lat,
              snapLng: snap[0],
              snapLat: snap[1],
            })
          } else {
            snapCoordsRef.current.delete(capturedWpIdForDrag)
          }
          refreshSnapLinesRef.current()

          // Only update heading arrows for the marker actively being dragged.
          // Gate on draggingWaypointIdRef (set in dragstart, cleared in dragend)
          // rather than pendingHeadingWaypointIdRef: the latter is re-synced from
          // the React prop on every render (see its assignment near the top), so
          // the move-start re-render — which never sets the pending *state* —
          // would clobber it back to null mid-drag and silently stop recalc.
          if (draggingWaypointIdRef.current !== capturedWpIdForDrag) return
          // No lanes at the current drag position falls back to the four
          // cardinal arrows, so the marker never loses its selection UI.
          const suggestions = headingSelectionBearings(result)
          // The first tick keeps an existing heading; one without a heading
          // counts as recalculated so a single direction is assigned at once.
          const recalculated =
            lastSuggestions === null
              ? keptHeading === null
              : !isSameSuggestionSet(lastSuggestions, suggestions)
          lastSuggestions = suggestions
          if (recalculated) {
            // New directions under the marker: a single one is assigned, several
            // remove the heading. Committed to state right away (not on release)
            // so the waypoint list follows; the markers effect preserves this
            // in-flight marker, as it does for onMarkerMoveStart.
            if (result.directions.length === 1) {
              keptHeading = result.directions[0].bearing
              onHeadingSelectRef.current?.(capturedWpIdForDrag, keptHeading)
            } else if (keptHeading !== null) {
              keptHeading = null
              onRemoveHeadingRef.current?.(capturedWpIdForDrag)
            }
          } else if (keptHeading !== null) {
            // Same lane: let the heading follow it as it curves. Published only
            // when the bearing changes (once per lane segment, not per tick).
            const nextHeading =
              matchingSuggestedBearing(suggestions, keptHeading) ?? keptHeading
            if (nextHeading !== keptHeading) {
              keptHeading = nextHeading
              onHeadingSelectRef.current?.(capturedWpIdForDrag, keptHeading)
            }
          }
          if (keptHeading !== null) {
            addConfirmedHeadingArrow(
              marker.getElement(),
              keptHeading,
              UNVALIDATED_COLOR,
            )
            return
          }
          showHeadingSelectionArrows(marker.getElement(), {
            strokeColor: UNVALIDATED_COLOR,
            availableDirections: suggestions,
            onHeadingSelect: bearing => {
              onHeadingSelectRef.current?.(capturedWpIdForDrag, bearing)
            },
            hasExistingHeading: false,
            onRemoveHeading: () => {
              onRemoveHeadingRef.current?.(capturedWpIdForDrag)
            },
          })
        }

        marker.on('dragstart', () => {
          draggingWaypointIdRef.current = capturedWpIdForDrag
          // Set ref directly so the 'drag' handler shows arrows during movement.
          // Do NOT call onMarkerDragStartRef here — that triggers React state
          // changes which schedule effects that can rebuild markers mid-drag,
          // releasing MapLibre's pointer capture and snapping the marker back.
          pendingHeadingWaypointIdRef.current = capturedWpIdForDrag
          keptHeading = headingsRef.current[capturedWpIdForDrag] ?? null
          lastSuggestions = null
          // Remove any existing heading arrows imperatively (no React state change).
          removeHeadingArrowsFromMarker(marker.getElement())
          // Moving the waypoint invalidates the generated route and any assigned
          // ODD element, so switch this marker to the orange "ungenerated" look
          // (with pending-selection glow) immediately — erasing the ODD pill and
          // recoloring in place. This must NOT rebuild the inner circle element:
          // it holds MapLibre's pointer capture (rebuilding snaps the marker back)
          // and hosts the heading-arrow overlays that recalculate live.
          markMarkerUngeneratedInPlace(marker.getElement(), isCompleted)
          // Show the selection arrows right away — the heading-arrow effect skips
          // the in-flight marker, so nothing else applies them on the first move.
          updateDragHeading()
          // Reset the route to the ungenerated state now, at move start. The
          // markers effect preserves this in-flight marker (see draggingWaypointIdRef
          // guard), so the state change won't tear it down mid-drag.
          onMarkerMoveStartRef.current?.(capturedWpIdForDrag)
        })

        marker.on('drag', updateDragHeading)

        marker.on('dragend', () => {
          draggingWaypointIdRef.current = null
          const pos = marker.getLngLat()
          // Now safe to update React state — drag is complete, no risk of
          // effect-driven marker reconstruction releasing pointer capture.
          // Both calls batch in React 18: heading state updates first, then
          // position, so effects see the final combined state in one render.
          // A heading still set at release is saved with the final bearing;
          // otherwise the waypoint goes back to pending selection.
          if (keptHeading !== null) {
            onHeadingSelectRef.current?.(capturedWpIdForDrag, keptHeading)
          } else {
            onMarkerDragStartRef.current?.(capturedWpIdForDrag)
          }
          onMarkerDragRef.current?.(capturedIdx, pos.lat, pos.lng)
        })
      }

      markersRef.current.push(marker)
      markerWaypointIdsRef.current.push(wp.id)
    }

    // The dragged waypoint vanished from the list (e.g. removed mid-drag) — drop
    // its preserved marker so it doesn't leak on the map.
    if (preservedMarker) preservedMarker.remove()
  }, [waypoints])

  // Ensure the snap-line MapLibre source+layer exist, then push current snapCoordsRef data.
  // Called after any change to snapCoordsRef. Safe to call multiple times.
  const refreshSnapLines = useCallback(() => {
    const map = mapRef.current
    if (!map || !mapLoadedRef.current) return

    const entries = [...snapCoordsRef.current.values()].filter(
      e =>
        Math.abs(e.fromLat - e.snapLat) >= 0.00002 ||
        Math.abs(e.fromLng - e.snapLng) >= 0.00002,
    )

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features: entries.map(e => ({
        type: 'Feature' as const,
        properties: {},
        geometry: {
          type: 'LineString' as const,
          coordinates: [
            [e.fromLng, e.fromLat],
            [e.snapLng, e.snapLat],
          ],
        },
      })),
    }

    if (map.getSource(HEADING_SNAP_SOURCE_ID)) {
      ;(
        map.getSource(HEADING_SNAP_SOURCE_ID) as maplibregl.GeoJSONSource
      ).setData(geojson)
    } else {
      map.addSource(HEADING_SNAP_SOURCE_ID, { type: 'geojson', data: geojson })
      map.addLayer({
        id: HEADING_SNAP_LAYER_ID,
        type: 'line',
        source: HEADING_SNAP_SOURCE_ID,
        paint: {
          'line-color': '#888888',
          'line-width': 3,
          'line-opacity': 0.85,
          'line-dasharray': [2, 3],
        },
        layout: { 'line-join': 'round', 'line-cap': 'round' },
      })
    }
  }, [])

  // Keep the ref in sync so drag handlers can call it without a stale closure.
  refreshSnapLinesRef.current = refreshSnapLines

  // Apply heading arrows and snap lines whenever headings or pending selection state changes.
  // Snap lines are computed for all waypoints that have any heading (confirmed or pending)
  // so they persist after a direction is chosen and appear on saved routes.
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    // Recompute snap coords for every waypoint that has a heading or is pending selection.
    const newCoords = new Map<string, SnapEntry>()

    for (const wp of waypoints) {
      if (wp.lat == null || wp.lng == null) continue
      // Mid-drag heading changes re-run this effect while the waypoint still
      // holds its pre-drag position; keep the snap line the drag handler drew.
      if (draggingWaypointIdRef.current === wp.id) {
        const live = snapCoordsRef.current.get(wp.id)
        if (live) newCoords.set(wp.id, live)
        continue
      }
      const hasPending = pendingHeadingWaypointId === wp.id
      const hasConfirmed = headings[wp.id] !== undefined
      if (!hasPending && !hasConfirmed) continue

      const lngLat = { lng: wp.lng, lat: wp.lat }
      let result = findNearbyStreetDirectionsFromSource(map, lngLat)
      if (result.directions.length === 0) {
        result = findNearbyStreetDirections(map, lngLat)
      }
      if (result.snapPoint) {
        newCoords.set(wp.id, {
          fromLng: wp.lng,
          fromLat: wp.lat,
          snapLng: result.snapPoint[0],
          snapLat: result.snapPoint[1],
        })
      }
    }

    snapCoordsRef.current = newCoords
    refreshSnapLines()

    // Precompute directions for the pending waypoint for the arrows.
    let pendingDirections: number[] = []
    const pendingWp = pendingHeadingWaypointId
      ? waypoints.find(w => w.id === pendingHeadingWaypointId)
      : undefined
    if (pendingWp?.lng != null && pendingWp?.lat != null) {
      const lngLat = { lng: pendingWp.lng, lat: pendingWp.lat }
      let result = findNearbyStreetDirectionsFromSource(map, lngLat)
      if (result.directions.length === 0) {
        result = findNearbyStreetDirections(map, lngLat)
      }
      pendingDirections = headingSelectionBearings(result)

      // A lane that allows only one direction (e.g. one-way) leaves nothing to
      // choose: confirm it right away. This covers dropping a new waypoint and
      // releasing a dragged one (the drag clears its heading). A waypoint that
      // already has a heading still opens the arrows so it can be removed.
      if (
        result.directions.length === 1 &&
        headings[pendingWp.id] === undefined
      ) {
        onHeadingSelectRef.current?.(pendingWp.id, result.directions[0].bearing)
        return
      }
    }

    // Update heading arrow overlays on each marker.
    for (let i = 0; i < markersRef.current.length; i++) {
      const marker = markersRef.current[i]
      const waypointId = markerWaypointIdsRef.current[i]
      if (!marker || !waypointId) continue

      const el = marker.getElement()
      const wp = waypoints.find(w => w.id === waypointId)
      if (!wp) continue

      const strokeColor = wp.validated ? VALIDATED_COLOR : UNVALIDATED_COLOR
      const isPending = pendingHeadingWaypointId === waypointId
      const confirmedHeading = headings[waypointId]

      if (draggingWaypointIdRef.current === waypointId) {
        // Never mutate the marker DOM while a drag is in progress — any DOM
        // change (even removing children) can release MapLibre's pointer capture
        // and interrupt the drag. The 'drag' event handler updates arrows live.
        continue
      }

      if (isPending) {
        // Add glow/pulse animation to the pending waypoint marker
        const circleEl = el.querySelector('div') as HTMLElement | null
        if (circleEl) {
          const glowColor = wp.validated
            ? 'rgba(5, 50, 255, 0.7)'
            : 'rgba(232, 119, 34, 0.7)'
          circleEl.style.setProperty('--waypoint-glow-color', glowColor)
          circleEl.style.animation = 'waypoint-pulse 1.2s ease-in-out infinite'
          circleEl.style.filter = `drop-shadow(0 0 6px ${glowColor})`
        }

        // pendingDirections is never empty: with no lane nearby it holds the
        // four cardinal placeholders, so the selection stays open.
        showHeadingSelectionArrows(el, {
          strokeColor,
          availableDirections: pendingDirections,
          onHeadingSelect: bearing => {
            onHeadingSelectRef.current?.(waypointId, bearing)
          },
          hasExistingHeading: confirmedHeading !== undefined,
          onRemoveHeading: () => {
            onRemoveHeadingRef.current?.(waypointId)
          },
        })
      } else if (confirmedHeading !== undefined) {
        // Remove glow when heading is confirmed
        const circleEl = el.querySelector('div') as HTMLElement | null
        if (circleEl) {
          circleEl.style.animation = ''
          circleEl.style.filter = ''
          circleEl.style.removeProperty('--waypoint-glow-color')
        }
        addConfirmedHeadingArrow(el, confirmedHeading, strokeColor)
      } else {
        // Remove glow when no heading state
        const circleEl = el.querySelector('div') as HTMLElement | null
        if (circleEl) {
          circleEl.style.animation = ''
          circleEl.style.filter = ''
          circleEl.style.removeProperty('--waypoint-glow-color')
        }
        removeHeadingArrowsFromMarker(el)
      }
    }
  }, [headings, pendingHeadingWaypointId, waypoints, refreshSnapLines])

  // Update ODD element markers + zoom-dependent visibility
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    for (const m of oddMarkersRef.current) m.remove()
    oddMarkersRef.current = []

    for (const element of oddElements) {
      const el = createOddElementMarker(element.type, element.name)

      // Set flag on pointerdown — this fires before MapLibre's internal pointer
      // tracking completes and before map.on('click') fires. The DOM 'click' event
      // fires after MapLibre's click handler, so a flag set there would be too late.
      el.addEventListener('pointerdown', e => {
        e.stopPropagation()
        oddMarkerClickedRef.current = true
        requestAnimationFrame(() => {
          oddMarkerClickedRef.current = false
        })
      })
      el.addEventListener('click', e => {
        e.stopPropagation()
        onOddElementClickRef.current?.(element)
      })

      const marker = new maplibregl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat([element.location.longitude, element.location.latitude])
        .addTo(map)

      oddMarkersRef.current.push(marker)
    }

    const updateOddVisibility = () => {
      const display = map.getZoom() >= 12 ? '' : 'none'
      for (const m of oddMarkersRef.current) {
        m.getElement().style.display = display
      }
    }
    map.on('zoom', updateOddVisibility)
    updateOddVisibility()

    return () => {
      map.off('zoom', updateOddVisibility)
    }
  }, [oddElements])

  // Update route line
  const updateRouteLine = useCallback((coordinates: [number, number][]) => {
    const map = mapRef.current
    if (!map || !mapLoadedRef.current) return

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features:
        coordinates.length >= 2
          ? [
              {
                type: 'Feature',
                properties: {},
                geometry: {
                  type: 'LineString',
                  coordinates,
                },
              },
            ]
          : [],
    }

    const source = map.getSource(ROUTE_SOURCE_ID) as
      | maplibregl.GeoJSONSource
      | undefined
    // Respect the reveal gate so a route line (re)built while hidden — e.g. the
    // polyline arriving during the map expand — stays invisible until runFit
    // reveals it in step with the camera fit. A CSS-like transition on the paint
    // property gives the fade; MapLibre interpolates line-opacity changes.
    const revealedOpacity = routeVisibleRef.current ? 0.8 : 0

    if (source) {
      source.setData(geojson)
      map.setPaintProperty(ROUTE_LAYER_ID, 'line-color', VALIDATED_COLOR)
      map.setPaintProperty(ROUTE_LAYER_ID, 'line-opacity', revealedOpacity)
    } else {
      map.addSource(ROUTE_SOURCE_ID, { type: 'geojson', data: geojson })
      map.addLayer({
        id: ROUTE_LAYER_ID,
        type: 'line',
        source: ROUTE_SOURCE_ID,
        paint: {
          'line-color': VALIDATED_COLOR,
          'line-width': 4,
          'line-opacity': revealedOpacity,
          'line-opacity-transition': { duration: ROUTE_REVEAL_MS },
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
      })
      // Move any already-loaded overlay layers (lanes, geonets) below the route line.
      // They were added before the route existed so couldn't use insertBeforeLayer at the time.
      for (const layer of map.getStyle().layers) {
        if (
          layer.id !== ROUTE_LAYER_ID &&
          (layer.id.endsWith('-line') ||
            layer.id.endsWith('-fill') ||
            layer.id.endsWith('-outline'))
        ) {
          map.moveLayer(layer.id, ROUTE_LAYER_ID)
        }
      }
      // Keep the snap-to-lane dashes above the route line so they aren't obscured by it.
      if (map.getLayer(HEADING_SNAP_LAYER_ID)) {
        map.moveLayer(HEADING_SNAP_LAYER_ID)
      }
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (mapLoadedRef.current) {
      updateRouteLine(routeCoordinates)
      return
    }

    const onLoad = () => updateRouteLine(routeCoordinates)
    map.on('load', onLoad)
    return () => {
      map.off('load', onLoad)
    }
  }, [routeCoordinates, updateRouteLine])

  // Update violation lines (red overlay on violated segments) + per-leg markers
  const updateViolationLines = useCallback(
    (currentViolations: RouteViolation[]) => {
      const map = mapRef.current
      if (!map || !mapLoadedRef.current) return

      // Remove previous violation markers
      for (const m of violationMarkersRef.current) m.remove()
      violationMarkersRef.current = []

      const features: GeoJSON.Feature[] = currentViolations
        .filter(v => v.coordinates && v.coordinates.length >= 2)
        .map(v => ({
          type: 'Feature',
          properties: {},
          geometry: {
            type: 'LineString',
            coordinates: v.coordinates!,
          },
        }))

      const geojson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features,
      }

      const source = map.getSource(VIOLATION_SOURCE_ID) as
        | maplibregl.GeoJSONSource
        | undefined
      if (source) {
        source.setData(geojson)
      } else {
        map.addSource(VIOLATION_SOURCE_ID, { type: 'geojson', data: geojson })
        map.addLayer({
          id: VIOLATION_LAYER_ID,
          type: 'line',
          source: VIOLATION_SOURCE_ID,
          paint: {
            'line-color': ERROR_COLOR,
            'line-width': 5,
            'line-opacity': 0.9,
          },
          layout: {
            'line-join': 'round',
            'line-cap': 'round',
          },
        })
      }

      // Group violations by leg so we place one marker per leg
      const byLeg = new Map<number, RouteViolation[]>()
      for (const v of currentViolations) {
        const key = v.legIndex ?? -1
        const arr = byLeg.get(key) ?? []
        arr.push(v)
        byLeg.set(key, arr)
      }

      for (const [legKey, legViolations] of byLeg.entries()) {
        // Find the violation with the most coordinate points — place marker at its midpoint
        let best = legViolations[0]
        for (const v of legViolations) {
          if ((v.coordinates?.length ?? 0) > (best.coordinates?.length ?? 0))
            best = v
        }
        const coords = best.coordinates as [number, number][] | null

        let markerLng: number
        let markerLat: number
        if (coords && coords.length >= 2) {
          ;[markerLng, markerLat] = calculateLineMidpoint(coords)
        } else if (coords && coords.length === 1) {
          ;[markerLng, markerLat] = coords[0]
        } else {
          // No geolocation data — fall back to midpoint between adjacent waypoints
          const wps = waypointsRef.current
          const legIdx = legKey < 0 ? 0 : legKey
          const wpA = wps[legIdx]
          const wpB = wps[legIdx + 1]
          if (
            wpA?.lat == null ||
            wpA?.lng == null ||
            wpB?.lat == null ||
            wpB?.lng == null
          )
            continue
          markerLng = (wpA.lng + wpB.lng) / 2
          markerLat = (wpA.lat + wpB.lat) / 2
        }

        const messages = legViolations.map(
          v => `MOIA: ${ROUTE_VIOLATION_LABELS[v.type] ?? v.type}`,
        )

        const el = document.createElement('div')
        el.style.cursor = 'pointer'
        el.style.width = '28px'
        el.style.height = '28px'
        el.style.display = 'flex'
        el.style.alignItems = 'center'
        el.style.justifyContent = 'center'
        el.style.background = 'white'
        el.style.borderRadius = '50%'
        el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)'
        el.style.zIndex = '150'
        el.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="${ERROR_COLOR}"/>
        </svg>`

        // Deduplicate messages by label before building popup HTML
        const uniqueMessages = [...new Set(messages)]

        const popup = new maplibregl.Popup({
          offset: 15,
          closeButton: false,
          closeOnClick: false,
        }).setHTML(
          `<div style="padding:4px 8px;max-width:300px">${uniqueMessages.map(m => `<div style="color:${ERROR_COLOR};font-size:12px;line-height:1.4">${m}</div>`).join('')}</div>`,
        )

        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([markerLng, markerLat])
          .setPopup(popup)
          .addTo(map)

        el.addEventListener('mouseenter', () => {
          el.style.zIndex = '300'
          marker.togglePopup()
          const popupEl = popup.getElement()
          if (popupEl) popupEl.style.zIndex = '400'
        })
        el.addEventListener('mouseleave', () => {
          el.style.zIndex = '150'
          marker.togglePopup()
        })

        violationMarkersRef.current.push(marker)
      }
    },
    [],
  )

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (mapLoadedRef.current) {
      updateViolationLines(violations)
      return
    }

    const onLoad = () => updateViolationLines(violations)
    map.on('load', onLoad)
    return () => {
      map.off('load', onLoad)
    }
  }, [violations, updateViolationLines])

  // Update compare lines (desired → snapped)
  const updateCompareLines = useCallback((wps: Waypoint[]) => {
    const map = mapRef.current
    if (!map || !mapLoadedRef.current) return

    const features: GeoJSON.Feature[] = []
    for (const wp of wps) {
      if (
        !wp.validated ||
        wp.desiredLat == null ||
        wp.desiredLng == null ||
        wp.lat == null ||
        wp.lng == null
      )
        continue

      // Only show line if desired and snapped differ meaningfully (>5m)
      const dLat = wp.desiredLat - wp.lat
      const dLng = wp.desiredLng - wp.lng
      if (Math.abs(dLat) < 0.00005 && Math.abs(dLng) < 0.00005) continue

      features.push({
        type: 'Feature',
        properties: {},
        geometry: {
          type: 'LineString',
          coordinates: [
            [wp.desiredLng, wp.desiredLat],
            [wp.lng, wp.lat],
          ],
        },
      })
    }

    const geojson: GeoJSON.FeatureCollection = {
      type: 'FeatureCollection',
      features,
    }

    const source = map.getSource(COMPARE_SOURCE_ID) as
      | maplibregl.GeoJSONSource
      | undefined
    if (source) {
      source.setData(geojson)
    } else {
      map.addSource(COMPARE_SOURCE_ID, { type: 'geojson', data: geojson })
      map.addLayer({
        id: COMPARE_LAYER_ID,
        type: 'line',
        source: COMPARE_SOURCE_ID,
        paint: {
          'line-color': '#888888',
          'line-width': 2,
          'line-opacity': 0.6,
          'line-dasharray': [4, 4],
        },
        layout: {
          'line-join': 'round',
          'line-cap': 'round',
        },
      })
    }
  }, [])

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (mapLoadedRef.current) {
      updateCompareLines(waypoints)
      return
    }

    const onLoad = () => updateCompareLines(waypoints)
    map.on('load', onLoad)
    return () => {
      map.off('load', onLoad)
    }
  }, [waypoints, updateCompareLines])

  // Update SDS violation lines + markers
  const updateSdsViolations = useCallback(
    (currentSdsViolations: SdsViolation[]) => {
      const map = mapRef.current
      if (!map || !mapLoadedRef.current) return

      // Clean up previous markers
      for (const m of sdsMarkersRef.current) m.remove()
      sdsMarkersRef.current = []

      const features: GeoJSON.Feature[] = currentSdsViolations
        .filter(v => v.coordinates.length >= 2)
        .map(v => ({
          type: 'Feature',
          properties: {},
          geometry: { type: 'LineString', coordinates: v.coordinates },
        }))

      const geojson: GeoJSON.FeatureCollection = {
        type: 'FeatureCollection',
        features,
      }

      const source = map.getSource(SDS_VIOLATION_SOURCE_ID) as
        | maplibregl.GeoJSONSource
        | undefined
      if (source) {
        source.setData(geojson)
      } else {
        map.addSource(SDS_VIOLATION_SOURCE_ID, {
          type: 'geojson',
          data: geojson,
        })
        map.addLayer({
          id: SDS_VIOLATION_LAYER_ID,
          type: 'line',
          source: SDS_VIOLATION_SOURCE_ID,
          paint: {
            'line-color': SDS_ERROR_COLOR,
            'line-width': 4.5,
            'line-opacity': 0.9,
          },
          layout: { 'line-join': 'round', 'line-cap': 'round' },
        })
      }

      // Add error markers at midpoints
      for (const v of currentSdsViolations) {
        if (v.coordinates.length < 2) continue
        const [lng, lat] = calculateLineMidpoint(v.coordinates)

        const el = document.createElement('div')
        el.style.cursor = 'pointer'
        el.style.width = '28px'
        el.style.height = '28px'
        el.style.display = 'flex'
        el.style.alignItems = 'center'
        el.style.justifyContent = 'center'
        el.style.background = 'white'
        el.style.borderRadius = '50%'
        el.style.boxShadow = '0 2px 8px rgba(0,0,0,0.3)'
        el.innerHTML = `<svg width="24" height="24" viewBox="0 0 24 24" fill="none">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="${SDS_ERROR_COLOR}"/>
        </svg>`

        const reasonLabel = SDS_REASON_LABELS[v.reason] ?? 'Unknown'
        const popupContent = document.createElement('div')
        popupContent.style.cssText = 'padding:4px 8px;max-width:300px'
        const header = document.createElement('strong')
        header.style.color = SDS_ERROR_COLOR
        header.textContent = 'Mobileye'
        popupContent.appendChild(header)
        popupContent.appendChild(document.createElement('br'))
        const reasonEl = document.createTextNode(reasonLabel)
        popupContent.appendChild(reasonEl)
        if (v.message) {
          popupContent.appendChild(document.createElement('br'))
          const contextEl = document.createTextNode(`Context: ${v.message}`)
          popupContent.appendChild(contextEl)
        }
        if (v.mapName) {
          popupContent.appendChild(document.createElement('br'))
          const mapEl = document.createTextNode(`Map: ${v.mapName}`)
          popupContent.appendChild(mapEl)
        }
        const popup = new maplibregl.Popup({
          offset: 15,
          closeButton: false,
          closeOnClick: false,
        }).setDOMContent(popupContent)

        const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([lng, lat])
          .setPopup(popup)
          .addTo(map)

        el.addEventListener('mouseenter', () => marker.togglePopup())
        el.addEventListener('mouseleave', () => marker.togglePopup())

        sdsMarkersRef.current.push(marker)
      }
    },
    [],
  )

  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (mapLoadedRef.current) {
      updateSdsViolations(sdsViolations)
      return
    }

    const onLoad = () => updateSdsViolations(sdsViolations)
    map.on('load', onLoad)
    return () => {
      map.off('load', onLoad)
    }
  }, [sdsViolations, updateSdsViolations])

  // Driver voice notes: severity-coloured mic markers, an optional density
  // heatmap and the hover popup, all owned by the shared annotations lib so this
  // map and the trip-history map render the same layer from the same code.
  // `belowLayerId` keeps the heat directly beneath the route line (i.e. above the
  // lane/geonet/ODD overlays, which useMapOverlays keeps re-floating to just
  // under that same line). No `buildTripHref` for now: the popup's "Open trip"
  // link is switched off until it is wanted back. Passing
  // `tripId => RouteBuilderAreaRoutes.EditTrip.route.replace(':id', tripId)`
  // restores it.
  //
  // The notes are held back while the map resizes or its camera moves. Opening
  // the editor slides the map column and flies the camera to the route, and
  // building hundreds of markers in those same frames made it stutter. The
  // heatmap draws the same held-back list, so it waits too.
  const settledAnnotations = useItemsAfterMapSettles(mapInstance, annotations)
  const { annotationLayer } = useAnnotationMapLayer({
    map: mapInstance,
    annotations: settledAnnotations,
    showHeatmap: showAnnotationHeatmap,
    belowLayerId: ROUTE_LAYER_ID,
  })

  // Update vehicle marker
  useEffect(() => {
    const map = mapRef.current
    if (!map) return

    if (vehicleMarkerRef.current) {
      vehicleMarkerRef.current.remove()
      vehicleMarkerRef.current = null
    }

    if (!vehicleMarker) return

    const el = document.createElement('div')
    el.style.cssText = 'width:55px;height:55px;cursor:default'
    el.title = vehicleMarker.name
    const img = document.createElement('img')
    img.src = vehicleMarker.isPhantom
      ? '/assets/images/vehicle-monitoring/phantom.png'
      : '/assets/images/vehicle-monitoring/vehicle.png'
    const rot = vehicleMarker.heading ?? 0
    img.style.cssText = `display:block;width:100%;height:100%;object-fit:contain;pointer-events:none;transform-origin:center;transform:rotate(${rot}deg)`
    el.appendChild(img)

    const popup = new maplibregl.Popup({
      offset: 20,
      closeButton: false,
    }).setText(vehicleMarker.name)

    const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
      .setLngLat([
        vehicleMarker.location.longitude,
        vehicleMarker.location.latitude,
      ])
      .setPopup(popup)
      .addTo(map)

    vehicleMarkerRef.current = marker
  }, [vehicleMarker])

  return (
    <>
      <div
        ref={containerRef}
        css={{
          width: '100%',
          height: '100%',
          minHeight: '400px',
          isolation: 'isolate',
        }}
      />
      {/*
        The annotation markers, heat layer and hover popups. Rendered as a
        sibling of the map container above rather than inside it, on purpose: the
        map div is `isolation: isolate`, so nothing MapLibre draws inside it can
        be lifted above the numbered waypoint markers by z-index alone. The
        lib's overlay layer lands next to it -- both become children of the
        shared `mapColumnRef` wrapper in `RouteBuilderAreaContentView.tsx`, since
        this component returns a Fragment with no wrapping element of its own --
        and covers the same box at a higher zIndex. See `useAnnotationMapLayer`
        for the full write-up.
      */}
      {annotationLayer}
    </>
  )
}
